import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import { FormField, MonthDateField, SelectFormField } from "../../../utils/FormFields";
import { showToast } from "../../../utils/Helper";
import { formattedNumber } from "../../FormattedNumber/FormattedNumber";

/**
 * PersonIncomeModal
 *
 * Modal para cargar los montos de ingresos de una persona (Punto 1 del
 * requerimiento "Requerimiento perfil y onboarding").
 *
 * Layout con tabs horizontales (IRP, IVA, Salary, Balance, Extra): una sección
 * visible por vez. Un tipo se "incluye" en el envío cuando tiene datos cargados
 * (el tab muestra un ✓). Al confirmar devuelve vía `onSubmit` un payload
 * estructurado (con subtotales) y la colección `SDTPersonIncomes` lista para el
 * backend. NO hace la llamada al API.
 *
 * NOTA: en esta etapa los montos se cargan solo como inputs numéricos. El espacio
 * para el futuro adjunto de archivos por sección queda preparado/comentado.
 */

// FileType numérico que espera el backend por tipo de documento.
// TODO(backend): confirmar los valores numéricos exactos de cada FileType.
export const PERSON_INCOME_FILE_TYPES = {
  IRP: 1,
  IVA: 2,
  SALARY: 3,
  BALANCE: 4,
  EXTRA: 5,
};

// Reglas de filas dinámicas para IVA.
const IVA_MIN_ROWS = 6;

const ACCENT = "#37474f";

// Catálogo de tipos de documento (orden + ícono + label) que alimenta los tabs.
const DOC_TYPES = [
  { key: "irp", label: "IRP", icon: "fa-file-text-o" },
  { key: "iva", label: "IVA", icon: "fa-calculator" },
  { key: "salario", label: "Salary", icon: "fa-money" },
  { key: "balance", label: "Balance", icon: "fa-balance-scale", juridicalOnly: true },
  { key: "extra", label: "Extra Income", icon: "fa-line-chart" },
];

// Internal name EXACTO que devuelve GetPersonIncomesData por subtipo, mapeado a
// su sección/tab. Es la clave de negocio estable para distribuir los incomes
// (a diferencia del subtypeId numérico, que puede variar por ambiente).
const SUBTYPE_INTERNAL_NAMES = {
  irp: ["PIT", "IRP"],
  iva: ["VAT"],
  salario: ["Salary"],
  balance: ["BalanceSheet"],
  extra: ["ExtraIncome"],
};

// Secciones que componen el "annual income" (vs. la sección "extra").
const ANNUAL_SECTIONS = ["irp", "iva", "salario", "balance"];

// Flag que informa al backend qué se modificó respecto de los incomes ya
// cargados: 0=nada, 1=annual income (IRP/IVA/Salary/Balance), 2=extra income,
// 3=ambos. Un cambio = fila nueva válida agregada o fila del backend borrada.
const INCOME_CHANGE = { NONE: 0, ANNUAL: 1, EXTRA: 2, BOTH: 3 };

// --- helpers numéricos / de fecha ----------------------------------------

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Fecha mínima admitida: hoy menos `minMonths` meses (antigüedad parametrizable).
const getMinDate = (minMonths) => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() - minMonths, today.getDate());
};

// Año (ej. "2025") no inferior a la antigüedad permitida.
const isYearValid = (year, minMonths) => {
  const y = parseInt(year, 10);
  if (!year || Number.isNaN(y) || String(year).length !== 4) return false;
  return y >= getMinDate(minMonths).getFullYear();
};

// Mes/año "MM/YYYY" (ej. "09/2025") no inferior a la antigüedad permitida.
const isMonthValid = (monthYear, minMonths) => {
  if (!/^\d{2}\/\d{4}$/.test(monthYear || "")) return false;
  const [mm, yyyy] = monthYear.split("/").map((p) => parseInt(p, 10));
  if (mm < 1 || mm > 12) return false;
  const minDate = getMinDate(minMonths);
  const entered = new Date(yyyy, mm - 1, 1);
  const minMonthStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  return entered >= minMonthStart;
};

// Normaliza a YYYY-MM-DD para el campo FileDate del backend.
const yearToFileDate = (year) => `${year}-12-31`;
const monthToFileDate = (monthYear) => {
  const [mm, yyyy] = monthYear.split("/");
  // Fin de mes: new Date(año, mesBase1, 0) da el último día del mes indicado.
  const lastDay = new Date(Number(yyyy), Number(mm), 0).getDate();
  return `${yyyy}-${mm}-${String(lastDay).padStart(2, "0")}`;
};

// Formatea una fecha "YYYY-MM-DD" del backend a "DD/MM/YYYY" para mostrar.
// Devuelve "" si no hay fecha válida.
const formatDisplayDate = (isoDate) => {
  const m = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

// Vencido (solo para la marca visual): la fecha de expiración es <= hoy.
// NOTA: se calcula por fecha a propósito; NO se usa IncomeIsActive, que tiene
// otra lógica de negocio además del vencimiento.
const isExpired = (expirationDate) => {
  const m = String(expirationDate || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const exp = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return exp <= todayStart;
};

// Inverso de los anteriores: parsea el FileDate "YYYY-MM-DD" que devuelve el
// backend para reconstruir el input de año ("YYYY") o mes/año ("MM/YYYY").
const fileDateToYear = (fileDate) => (fileDate ? String(fileDate).slice(0, 4) : "");
const fileDateToMonth = (fileDate) => {
  const s = String(fileDate || "");
  return s.length >= 7 ? `${s.slice(5, 7)}/${s.slice(0, 4)}` : "";
};

// Adaptadores entre el estado "MM/YYYY" y el MonthDateField (picker mes/año).
// El picker parsea/emite fechas; usamos MM/DD/YYYY (parseo local) y getters
// locales para round-trip sin corrimientos de zona horaria.
const monthToPickerValue = (monthYear) => {
  const m = String(monthYear || "").match(/^(\d{2})\/(\d{4})$/);
  return m ? `${m[1]}/01/${m[2]}` : "";
};
const pickerToMonth = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Lee el primer campo definido entre varios nombres (tolera camelCase vs
// PascalCase según cómo serialice GeneXus el SDT: amount/Amount, fileDate/FileDate...).
const readField = (item, ...names) => {
  for (let i = 0; i < names.length; i += 1) {
    const v = item[names[i]];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
};

// Id incremental por fila para usar como key estable en React (evita los
// problemas de reordenar/borrar filas con keys basadas en índice).
let __rowUid = 0;
const nextRowUid = () => `row${(__rowUid += 1)}`;

const emptyIvaRows = (n, ccy = "") =>
  Array.from({ length: n }, () => ({ uid: nextRowUid(), month: "", amount: "", currencyId: ccy, cotizacion: "1" }));

const PersonIncomeModal = ({
  isOpen,
  toggle,
  onSubmit,
  onRecalculate,
  personType,
  minMonths = 24,
  currencyId = null,
  currencyLabel = "Gs",
  currencies = [],
  initialData = null,
}) => {
  // Persona Jurídica: solo Balance. Persona Física: el resto (IRP/IVA/Salary/Extra).
  const balanceAvailable = personType === "Juridical";
  const orderedKeys = DOC_TYPES.filter((t) =>
    balanceAvailable ? t.juridicalOnly : !t.juridicalOnly
  ).map((t) => t.key);

  // Fila nueva con uid estable + defaults (monto/moneda/quote) + campos de sección.
  const mkRow = (base) => ({ uid: nextRowUid(), amount: "", currencyId, cotizacion: "1", ...base });

  // Cada fila lleva su propia moneda (currencyId) y quote price (cotizacion),
  // además del monto. IRP/Salary/Balance multi-fila, igual que IVA/Extra.
  const [irpRows, setIrpRows] = useState(() => [mkRow({ year: "" })]);
  const [ivaRows, setIvaRows] = useState(() => emptyIvaRows(IVA_MIN_ROWS, currencyId));
  const [salarioRows, setSalarioRows] = useState(() => [mkRow({ month: "" })]);
  const [balanceRows, setBalanceRows] = useState(() => [mkRow({ year: "" })]);
  // Extra income: filas ilimitadas (concepto + mes + monto + moneda + quote).
  const [extraRows, setExtraRows] = useState(() => [mkRow({ concept: "", month: "" })]);
  const [errors, setErrors] = useState({});
  // Tras el primer intento de envío, re-validamos en vivo a medida que el usuario
  // corrige, para feedback inmediato (en lugar de validar solo al apretar el botón).
  const [submitted, setSubmitted] = useState(false);
  // Totales devueltos por RecalculatePersonIncomes (los calcula el backend).
  const [recalc, setRecalc] = useState(null);
  const [recalcLoading, setRecalcLoading] = useState(false);
  // Filtro "Relevant for calculation": encendido, muestra solo las filas del
  // backend con IncomeIsActive === true (las que el backend considera relevantes
  // para el cálculo). Las filas nuevas siempre se ven (se están cargando).
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  // Guardado (Confirm → SetPersonIncomesData en el padre).
  const [saving, setSaving] = useState(false);
  // Filas del backend que el usuario borró: se reenvían con Action "DLT".
  const [deletedIncomes, setDeletedIncomes] = useState([]);

  // Filas visibles según el switch, preservando el índice real (para no romper
  // los handlers/validación que indexan por posición en el estado).
  const visibleRows = (rows) =>
    rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !(showOnlyActive && row.fromBackend && row.isActive !== true));

  // ¿Hay algún registro NUEVO con datos (no viene del backend)? Las filas vacías
  // que sembramos como placeholder no cuentan; solo las que tienen monto o fecha.
  const rowHasData = (r) => toNumber(r.amount) > 0 || !!r.month || !!r.year || !!r.concept;
  const hasNewRecords = [irpRows, ivaRows, salarioRows, balanceRows, extraRows].some((rows) =>
    rows.some((r) => !r.fromBackend && rowHasData(r))
  );

  // Si aparecen registros nuevos, el switch se oculta: apagamos el filtro para no
  // dejarlo activo "invisible" (ocultaría las filas nuevas).
  useEffect(() => {
    if (hasNewRecords && showOnlyActive) setShowOnlyActive(false);
  }, [hasNewRecords, showOnlyActive]);

  // Tab activo (sección visible). Por defecto el primero.
  const [active, setActive] = useState(orderedKeys[0]);

  // Un tipo se "incluye" en el envío cuando tiene datos cargados.
  const isFilled = (key) => {
    switch (key) {
      case "irp":
        return irpRows.some((r) => toNumber(r.amount) > 0);
      case "iva":
        return ivaRows.some((r) => r.month || toNumber(r.amount) > 0);
      case "salario":
        return salarioRows.some((r) => toNumber(r.amount) > 0);
      case "balance":
        return balanceAvailable && balanceRows.some((r) => toNumber(r.amount) > 0);
      case "extra":
        return extraRows.some((r) => toNumber(r.amount) > 0);
      default:
        return false;
    }
  };

  // Una sección "tiene error" si existe alguna key de error con su prefijo
  // (irpYear, ivaMonth0, salarioAmount, extraConcept1, ...). Se usa para marcar
  // el tab aunque no esté activo, así los errores no quedan ocultos.
  const sectionHasError = (key) =>
    Object.keys(errors).some((k) => k !== "general" && k.toLowerCase().startsWith(key.toLowerCase()));

  // Los totales/ponderaciones (×12, ×13, annual income) los calcula el backend
  // a partir de la colección; el modal solo carga y envía los registros.

  // Al borrar una fila del backend, la guardamos con Action "DLT" para reenviarla
  // en el Confirm (que el backend la elimine). Las filas nuevas solo se descartan.
  const recordDeletion = (row) => {
    if (row && row.fromBackend && row.raw) {
      setDeletedIncomes((prev) => [...prev, { ...row.raw, Action: "DLT" }]);
    }
  };
  // Remueve una fila: las del backend siempre (registrando DLT); las nuevas solo
  // si se mantiene el mínimo de filas de la sección.
  const removeRow = (rows, setRows, index, minRows) => {
    const row = rows[index];
    if (!row) return;
    if (row.fromBackend) {
      recordDeletion(row);
      setRows(rows.filter((_, i) => i !== index));
    } else if (rows.length > minRows) {
      setRows(rows.filter((_, i) => i !== index));
    }
  };

  // --- IVA filas dinámicas (mín 6, sin máximo: se pueden cargar todas las que se quiera) ---
  const addIvaRow = () => setIvaRows((rows) => [...rows, mkRow({ month: "" })]);
  const removeIvaRow = (index) => removeRow(ivaRows, setIvaRows, index, IVA_MIN_ROWS);
  const updateIvaRow = (index, field, value) =>
    setIvaRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  // --- Extra income filas dinámicas (ilimitadas) ---
  const addExtraRow = () =>
    setExtraRows((rows) => [...rows, mkRow({ concept: "", month: "" })]);
  const removeExtraRow = (index) => removeRow(extraRows, setExtraRows, index, 1);
  const updateExtraRow = (index, field, value) =>
    setExtraRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  // --- IRP / Salary / Balance filas dinámicas (ilimitadas, mín 1) ---
  const addIrpRow = () => setIrpRows((rows) => [...rows, mkRow({ year: "" })]);
  const removeIrpRow = (index) => removeRow(irpRows, setIrpRows, index, 1);
  const updateIrpRow = (index, field, value) =>
    setIrpRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  const addSalarioRow = () =>
    setSalarioRows((rows) => [...rows, mkRow({ month: "" })]);
  const removeSalarioRow = (index) => removeRow(salarioRows, setSalarioRows, index, 1);
  const updateSalarioRow = (index, field, value) =>
    setSalarioRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  const addBalanceRow = () =>
    setBalanceRows((rows) => [...rows, mkRow({ year: "" })]);
  const removeBalanceRow = (index) => removeRow(balanceRows, setBalanceRows, index, 1);
  const updateBalanceRow = (index, field, value) =>
    setBalanceRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  // --- carga inicial: distribuye los incomes que ya vienen del backend ---
  // Cada registro se ubica en su sección comparando el subtypeInternalName contra
  // SUBTYPE_INTERNAL_NAMES (match case-insensitive). Tolera camelCase/PascalCase.
  const resolveSectionKey = (item) => {
    const name = String(
      readField(item, "subtypeInternalName", "SubtypeInternalName", "SubTypeInternalName") || ""
    ).toLowerCase();
    if (!name) return null;
    return (
      Object.keys(SUBTYPE_INTERNAL_NAMES).find((key) =>
        SUBTYPE_INTERNAL_NAMES[key].some((n) => n.toLowerCase() === name)
      ) || null
    );
  };

  const seedFromIncomes = (data) => {
    const list = Array.isArray(data)
      ? data
      : (data &&
          (data.SDTPersonIncomesCollection || data.SDTPersonIncomes || data.sdtPersonIncomes)) ||
        [];

    // Agrupo por sección. IRP/Salary/Balance son de un único registro en el
    // modal; IVA/Extra admiten varias filas.
    const bySection = { irp: [], iva: [], salario: [], balance: [], extra: [] };
    list.forEach((item) => {
      const key = resolveSectionKey(item);
      if (key && bySection[key]) bySection[key].push(item);
    });

    const num = (v) => (v === undefined || v === null ? "" : String(v));
    const fd = (item) => String(readField(item, "fileDate", "FileDate") || "");
    // Metadatos de la fila que viene del backend: archivo de respaldo (display),
    // flag fromBackend (read-only, solo se puede borrar) y el item crudo `raw`
    // para reenviarlo verbatim en el recalculate.
    const arch = (it) => ({
      uid: nextRowUid(),
      archiveName: readField(it, "archiveName", "ArchiveName") || "",
      archiveExt: readField(it, "archiveExtension", "ArchiveExtension") || "",
      currencyId: num(readField(it, "currencyId", "CurrencyId")),
      cotizacion: num(readField(it, "currencyQuotePrice", "CurrencyQuotePrice")),
      isActive: readField(it, "IncomeIsActive", "incomeIsActive") !== false,
      expirationDate: String(readField(it, "fileExpirationDate", "FileExpirationDate") || ""),
      fromBackend: true,
      raw: it,
    });

    const irpSeed = bySection.irp.map((it) => ({
      year: fileDateToYear(fd(it)),
      amount: num(readField(it, "amount", "Amount")),
      ...arch(it),
    }));
    setIrpRows(irpSeed.length > 0 ? irpSeed : [mkRow({ year: "" })]);

    const salSeed = bySection.salario.map((it) => ({
      month: fileDateToMonth(fd(it)),
      amount: num(readField(it, "amount", "Amount")),
      ...arch(it),
    }));
    setSalarioRows(salSeed.length > 0 ? salSeed : [mkRow({ month: "" })]);

    const balSeed = bySection.balance.map((it) => ({
      year: fileDateToYear(fd(it)),
      amount: num(readField(it, "amount", "Amount")),
      ...arch(it),
    }));
    setBalanceRows(balSeed.length > 0 ? balSeed : [mkRow({ year: "" })]);

    const nextIva = bySection.iva.map((it) => ({
      month: fileDateToMonth(fd(it)),
      amount: num(readField(it, "amount", "Amount")),
      ...arch(it),
    }));
    setIvaRows(
      nextIva.length >= IVA_MIN_ROWS
        ? nextIva
        : [...nextIva, ...emptyIvaRows(IVA_MIN_ROWS - nextIva.length, currencyId)]
    );

    const nextExtra = bySection.extra.map((it) => ({
      concept: readField(it, "description", "Description") || "",
      month: fileDateToMonth(fd(it)),
      amount: num(readField(it, "amount", "Amount")),
      ...arch(it),
    }));
    setExtraRows(
      nextExtra.length > 0 ? nextExtra : [mkRow({ concept: "", month: "" })]
    );

    setErrors({});
    setSubmitted(false);
    setSaving(false);
    setDeletedIncomes([]);
  };

  // Al abrir la modal, rellena las secciones con los incomes ya cargados.
  // Si no se pasa initialData (undefined/null) no toca el estado (backward-compatible).
  useEffect(() => {
    if (isOpen && initialData !== undefined && initialData !== null) {
      seedFromIncomes(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Validación. La antigüedad de las fechas se valida EN VIVO (apenas la fecha
  // está completa), para mostrar el mensaje antes de pasar al siguiente campo.
  // Los requeridos/montos se validan al confirmar (full = true). Extra Income
  // tiene fecha LIBRE (sin restricción de antigüedad). Las filas del backend son
  // read-only y no se validan.
  const MSG_YEAR = `Invalid year or older than ${minMonths} months.`;
  const MSG_MONTH = `Invalid month or older than ${minMonths} months.`;
  const MSG_CURRENCY = "Select a currency.";
  const MSG_QUOTE = "Enter a valid quote price.";
  const MSG_DUP_YEAR = "Duplicated year.";
  const MSG_DUP_MONTH = "Duplicated month.";

  // Cuenta ocurrencias de cada valor de fecha (año o mes/año) en TODAS las filas
  // de la sección (backend + nuevas), para detectar duplicados.
  const dupCounts = (rows, field) => {
    const counts = {};
    rows.forEach((r) => {
      const v = r[field];
      if (v) counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  };
  const yearComplete = (y) => /^\d{4}$/.test(y || "");
  const monthComplete = (m) => /^\d{2}\/\d{4}$/.test(m || "");

  const validate = (full = submitted) => {
    const e = {};

    // Antigüedad de fechas — en vivo, solo cuando la fecha está completa
    // (para no molestar mientras se tipea). Excepto Extra (fecha libre).
    irpRows.forEach((r, i) => {
      if (!r.fromBackend && yearComplete(r.year) && !isYearValid(r.year, minMonths)) e[`irpYear${i}`] = MSG_YEAR;
    });
    ivaRows.forEach((r, i) => {
      if (!r.fromBackend && monthComplete(r.month) && !isMonthValid(r.month, minMonths)) e[`ivaMonth${i}`] = MSG_MONTH;
    });
    salarioRows.forEach((r, i) => {
      if (!r.fromBackend && monthComplete(r.month) && !isMonthValid(r.month, minMonths)) e[`salarioMonth${i}`] = MSG_MONTH;
    });
    balanceRows.forEach((r, i) => {
      if (!r.fromBackend && yearComplete(r.year) && !isYearValid(r.year, minMonths)) e[`balanceYear${i}`] = MSG_YEAR;
    });

    // Requeridos y montos — al confirmar.
    if (full) {
      if (isFilled("irp")) {
        const dup = dupCounts(irpRows, "year");
        irpRows.forEach((r, i) => {
          if (r.fromBackend || (!r.year && !r.amount)) return;
          if (!isYearValid(r.year, minMonths)) e[`irpYear${i}`] = MSG_YEAR;
          else if (dup[r.year] > 1) e[`irpYear${i}`] = MSG_DUP_YEAR;
          if (toNumber(r.amount) <= 0) e[`irpAmount${i}`] = "Enter a valid amount.";
          if (!r.currencyId) e[`irpCurrency${i}`] = MSG_CURRENCY;
          if (toNumber(r.cotizacion) <= 0) e[`irpQuote${i}`] = MSG_QUOTE;
        });
      }
      if (isFilled("iva")) {
        const dup = dupCounts(ivaRows, "month");
        ivaRows.forEach((r, i) => {
          // Fila "vacía" si no tiene monto: al borrar el contenido, el picker de mes
          // puede quedar con un valor pegado, así que usamos el monto como señal real
          // de dato cargado. Sin monto → se ignora (no bloquea navegar de tab).
          if (r.fromBackend || toNumber(r.amount) <= 0) return;
          if (!isMonthValid(r.month, minMonths)) e[`ivaMonth${i}`] = MSG_MONTH;
          else if (dup[r.month] > 1) e[`ivaMonth${i}`] = MSG_DUP_MONTH;
          if (toNumber(r.amount) <= 0) e[`ivaAmount${i}`] = "Invalid amount.";
          if (!r.currencyId) e[`ivaCurrency${i}`] = MSG_CURRENCY;
          if (toNumber(r.cotizacion) <= 0) e[`ivaQuote${i}`] = MSG_QUOTE;
        });
        // Mínimo de registros IVA para confirmar: se cuentan las filas activas del
        // backend (IncomeIsActive) + las nuevas válidas cargadas. Solo se exige
        // cuando el usuario está modificando IVA (agrega filas nuevas o borra del
        // backend), para no bloquear un Confirm que no toca nada (no-op).
        const ivaNew = ivaRows.filter((r) => !r.fromBackend && r.month && toNumber(r.amount) > 0).length;
        const ivaActive = ivaRows.filter((r) => r.fromBackend && r.isActive === true).length;
        const ivaDeleted = deletedIncomes.some((it) => resolveSectionKey(it) === "iva");
        const ivaTotal = ivaNew + ivaActive;
        if ((ivaNew > 0 || ivaDeleted) && ivaTotal < IVA_MIN_ROWS) {
          e.minIvaCount = `IVA requires at least ${IVA_MIN_ROWS} records (active + new); currently ${ivaTotal}.`;
        }
      }
      if (isFilled("salario")) {
        const dup = dupCounts(salarioRows, "month");
        salarioRows.forEach((r, i) => {
          // Fila "vacía" si no tiene monto (el picker de mes puede quedar pegado al
          // borrar): usamos el monto como señal real de dato → sin monto no bloquea.
          if (r.fromBackend || toNumber(r.amount) <= 0) return;
          if (!isMonthValid(r.month, minMonths)) e[`salarioMonth${i}`] = MSG_MONTH;
          else if (dup[r.month] > 1) e[`salarioMonth${i}`] = MSG_DUP_MONTH;
          if (toNumber(r.amount) <= 0) e[`salarioAmount${i}`] = "Enter a valid amount.";
          if (!r.currencyId) e[`salarioCurrency${i}`] = MSG_CURRENCY;
          if (toNumber(r.cotizacion) <= 0) e[`salarioQuote${i}`] = MSG_QUOTE;
        });
      }
      if (isFilled("balance")) {
        const dup = dupCounts(balanceRows, "year");
        balanceRows.forEach((r, i) => {
          if (r.fromBackend || (!r.year && !r.amount)) return;
          if (!isYearValid(r.year, minMonths)) e[`balanceYear${i}`] = MSG_YEAR;
          else if (dup[r.year] > 1) e[`balanceYear${i}`] = MSG_DUP_YEAR;
          if (toNumber(r.amount) <= 0) e[`balanceAmount${i}`] = "Enter a valid amount.";
          if (!r.currencyId) e[`balanceCurrency${i}`] = MSG_CURRENCY;
          if (toNumber(r.cotizacion) <= 0) e[`balanceQuote${i}`] = MSG_QUOTE;
        });
      }
      if (isFilled("extra")) {
        extraRows.forEach((r, i) => {
          if (r.fromBackend || (!r.concept && !r.month && !r.amount)) return;
          if (!r.concept || !r.concept.trim()) e[`extraConcept${i}`] = "Enter a concept.";
          // Fecha libre: solo se exige que esté seleccionada, sin antigüedad.
          if (!monthComplete(r.month)) e[`extraMonth${i}`] = "Select a month.";
          if (toNumber(r.amount) <= 0) e[`extraAmount${i}`] = "Invalid amount.";
          if (!r.currencyId) e[`extraCurrency${i}`] = MSG_CURRENCY;
          if (toNumber(r.cotizacion) <= 0) e[`extraQuote${i}`] = MSG_QUOTE;
        });
      }
      if (!orderedKeys.some((k) => isFilled(k))) {
        e.general = "Load at least one document type.";
      }
    }

    setErrors(e);
    return e;
  };

  // Cambio de tab: valida el tab que se está dejando; si tiene errores, los
  // revela, avisa con un toast y bloquea el cambio hasta corregirlos.
  const goToTab = (key) => {
    if (key === active) return;
    const e = validate(true);
    const currentHasErrors = Object.keys(e).some(
      (k) => k !== "general" && k.toLowerCase().startsWith(active.toLowerCase())
    );
    if (currentHasErrors) {
      setSubmitted(true);
      const t = DOC_TYPES.find((d) => d.key === active);
      showToast(`Please fix the errors in the "${t ? t.label : active}" tab.`, false, true);
      return;
    }
    setActive(key);
  };

  // Agregar fila: no permite sumar una nueva si la sección actual tiene errores
  // (p.ej. la fila anterior está incompleta/inválida).
  const addRowIfValid = (key, addFn) => {
    const e = validate(true);
    const sectionHasErrors = Object.keys(e).some(
      (k) => k !== "general" && k.toLowerCase().startsWith(key.toLowerCase())
    );
    if (sectionHasErrors) {
      setSubmitted(true);
      showToast("Please complete the current rows before adding a new one.", false, true);
      return;
    }
    addFn();
  };

  // Re-validación en vivo: cada cambio en los inputs vuelve a validar. Antes de
  // confirmar solo muestra errores de antigüedad de fecha (fechas completas);
  // al confirmar (submitted) suma los requeridos/montos.
  useEffect(() => {
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, irpRows, ivaRows, salarioRows, balanceRows, extraRows]);

  // Los totales recalculados quedan obsoletos si cambian las filas → limpiarlos.
  useEffect(() => {
    setRecalc(null);
  }, [irpRows, ivaRows, salarioRows, balanceRows, extraRows]);

  // Moneda + quote price por fila (fallback a la moneda por defecto / quote 1).
  const rowCcy = (r) => ({
    CurrencyId: toNumber(r.currencyId) || currencyId,
    CurrencyQuotePrice: toNumber(r.cotizacion) || 1,
  });

  const handleConfirm = () => {
    setSubmitted(true);
    const e = validate(true);
    const keys = Object.keys(e);
    if (keys.length > 0) {
      // Lleva al usuario al primer tab con error y avisa (así no falla en silencio
      // si el error está en un tab que no está viendo, p.ej. el mínimo de IVA).
      const sectionOf = (k) =>
        k === "minIvaCount"
          ? "iva"
          : DOC_TYPES.map((d) => d.key).find((key) => k.toLowerCase().startsWith(key));
      const tab = keys.map(sectionOf).find(Boolean);
      if (tab && tab !== active) setActive(tab);
      showToast("Please fix the highlighted errors before confirming.", false, true);
      return;
    }

    // Solo cambios en el formato de GetPersonIncomesData: filas nuevas (Action
    // "INS") + filas del backend borradas (Action "DLT"). El padre persiste con
    // SetPersonIncomesData y muestra el toast de éxito/error.
    setSaving(true);
    onSubmit({ SDTPersonIncomesCollection: [...buildCollection(false), ...deletedIncomes] }, () => {
      setSaving(false);
    });
  };

  // Arma la colección en el formato de GetPersonIncomesData.
  // includeBackend=true (Recalculate): incluye las filas del backend verbatim
  // (raw) + las nuevas construidas → el back recalcula sobre el set completo.
  // includeBackend=false (Confirm/Set): SOLO las filas nuevas cargadas por el
  // usuario (las del backend ya están persistidas, no se reenvían).
  const buildCollection = (includeBackend) => {
    const items = [];
    const add = (key, rows, fileDateOf, isValid) => {
      rows.forEach((r) => {
        if (r.fromBackend) {
          if (includeBackend && r.raw) items.push(r.raw);
          return;
        }
        if (!isValid(r)) return;
        items.push({
          SubTypeInternalName: SUBTYPE_INTERNAL_NAMES[key][0],
          SubTypeId: "",
          FileDate: fileDateOf(r),
          FileExpirationDate: "",
          RegisterDate: "",
          Amount: toNumber(r.amount),
          ...rowCcy(r),
          Description: key === "extra" ? r.concept || "" : "",
          ArchiveId: "",
          ArchiveBlob: "",
          ArchiveName: "",
          ArchiveExtension: "",
          ArchiveTypeId: "",
          Action: "INS",
        });
      });
    };
    add("irp", irpRows, (r) => yearToFileDate(r.year), (r) => r.year && toNumber(r.amount) > 0);
    add("iva", ivaRows, (r) => monthToFileDate(r.month), (r) => r.month && toNumber(r.amount) > 0);
    add("salario", salarioRows, (r) => monthToFileDate(r.month), (r) => r.month && toNumber(r.amount) > 0);
    add("balance", balanceRows, (r) => yearToFileDate(r.year), (r) => r.year && toNumber(r.amount) > 0);
    add("extra", extraRows, (r) => monthToFileDate(r.month), (r) => r.month && toNumber(r.amount) > 0);
    return items;
  };

  // Determina qué categorías cambiaron respecto de los incomes ya cargados,
  // considerando tanto filas nuevas válidas como filas del backend borradas.
  // Devuelve el enum INCOME_CHANGE (0/1/2/3) para mandar al backend.
  const computeIncomeChangeType = () => {
    const hasNew = (rows, isValid) => rows.some((r) => !r.fromBackend && isValid(r));
    const annualNew =
      hasNew(irpRows, (r) => r.year && toNumber(r.amount) > 0) ||
      hasNew(ivaRows, (r) => r.month && toNumber(r.amount) > 0) ||
      hasNew(salarioRows, (r) => r.month && toNumber(r.amount) > 0) ||
      hasNew(balanceRows, (r) => r.year && toNumber(r.amount) > 0);
    const extraNew = hasNew(extraRows, (r) => r.month && toNumber(r.amount) > 0);

    const deletedSections = deletedIncomes.map((it) => resolveSectionKey(it));
    const annualDel = deletedSections.some((k) => ANNUAL_SECTIONS.includes(k));
    const extraDel = deletedSections.includes("extra");

    const annual = annualNew || annualDel;
    const extra = extraNew || extraDel;
    if (annual && extra) return INCOME_CHANGE.BOTH;
    if (annual) return INCOME_CHANGE.ANNUAL;
    if (extra) return INCOME_CHANGE.EXTRA;
    return INCOME_CHANGE.NONE;
  };

  // Pide al backend (RecalculatePersonIncomes) los totales a partir de la
  // colección actual, sin cerrar la modal. onRecalculate: (payload, callback).
  const handleRecalculate = () => {
    setSubmitted(true);
    if (Object.keys(validate(true)).length > 0) return;
    if (!onRecalculate) return;
    setRecalcLoading(true);
    onRecalculate(
      { SDTPersonIncomesCollection: buildCollection(true), IncomeChangeType: computeIncomeChangeType() },
      (resp) => {
        setRecalcLoading(false);
        if (resp) {
          setRecalc({ annual: resp.PersonAnualIncome, extra: resp.PersonExtraIncomeVolume });
        }
      }
    );
  };

  // Estilo de panel neutro reutilizado en el sistema (consistente con KYC tab).
  const sectionStyle = {
    border: "1px solid #d6d6d6",
    borderRadius: "8px",
    backgroundColor: "#f8f9fa",
    padding: "16px",
    marginBottom: "16px",
  };

  const tabStyle = (key) => {
    const isActive = active === key;
    return {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: 14,
      whiteSpace: "nowrap",
      fontWeight: isActive ? 600 : 500,
      color: isActive ? ACCENT : "#607d8b",
      borderBottom: isActive ? `2px solid ${ACCENT}` : "2px solid transparent",
      marginBottom: -1,
    };
  };

  // Columna "File": muestra el archivo de respaldo del backend como chip
  // (clip + nombre truncado) o "-" si la fila no tiene archivo. Solo lectura.
  const fileCell = (row, index) => {
    const fullName = row.archiveExt ? `${row.archiveName}.${row.archiveExt}` : row.archiveName;
    return (
      <Col style={{ marginBottom: "0.9rem" }}>
        {index === 0 && (
          <label style={{ fontWeight: 500, fontSize: 15, display: "block", textAlign: "center" }}>File</label>
        )}
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            minHeight: 34,
            gap: 6,
            padding: "4px 10px",
            background: "#f1f3f5",
            border: "1px solid #e3e8ec",
            borderRadius: 6,
            fontSize: 12,
            color: "#607d8b",
          }}
          title={row.archiveName ? fullName : undefined}
        >
          {row.archiveName && <i className="fa fa-paperclip" aria-hidden="true" />}
          {row.archiveName ? (
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fullName}</span>
          ) : (
            <span style={{ color: "#b0bec5" }}>-</span>
          )}
        </div>
      </Col>
    );
  };

  // Columna "Expiration date": fecha de vencimiento del registro (solo filas del
  // backend). Si el registro está vencido (isActive === false) se resalta en rojo
  // con un ícono de alerta; las filas nuevas aún no tienen vencimiento → "-".
  const expirationCell = (row, index) => {
    const expired = row.fromBackend && isExpired(row.expirationDate);
    const display = row.fromBackend ? formatDisplayDate(row.expirationDate) : "";
    return (
      <Col xs="auto" style={{ marginBottom: "0.9rem" }}>
        {index === 0 && (
          <label style={{ fontWeight: 500, fontSize: 15, display: "block", textAlign: "center" }}>Expiration</label>
        )}
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            minHeight: 34,
            gap: 6,
            padding: "4px 10px",
            minWidth: 104,
            background: expired ? "#fdecea" : "#f1f3f5",
            border: `1px solid ${expired ? "#f5c6cb" : "#e3e8ec"}`,
            borderRadius: 6,
            fontSize: 12,
            color: expired ? "#c0392b" : "#607d8b",
            fontWeight: expired ? 600 : 400,
          }}
          title={expired ? `Expired on ${display}` : display ? `Valid until ${display}` : undefined}
        >
          {expired && <i className="fa fa-exclamation-triangle" aria-hidden="true" />}
          {display ? (
            <span style={{ whiteSpace: "nowrap" }}>{display}</span>
          ) : (
            <span style={{ color: "#b0bec5" }}>-</span>
          )}
        </div>
      </Col>
    );
  };

  // Columna final "borrar": todas alineadas al final, en línea.
  const trashCell = (onRemove, disabled) => (
    <Col xs="auto" style={{ marginBottom: "0.9rem" }} className="d-flex align-items-center justify-content-start">
      <Button
        color="link"
        className="text-danger p-0"
        disabled={disabled}
        onClick={onRemove}
        title="Remove row"
        aria-label="Remove row"
      >
        <i className="fa fa-trash" />
      </Button>
    </Col>
  );

  // Monto + moneda (dropdown ISO) + quote price, para una fila de cualquier tipo.
  // amountMd permite achicar el monto en secciones con más campos (ej. Extra).
  const amountCols = (key, row, index, update, amountMd = "3") => (
    <React.Fragment>
      <Col md={amountMd}>
        <FormField
          label={index === 0 ? "Amount" : ""}
          name={`${key}Amount${index}`}
          type="number"
          dataTypeDecimalName="currency"
          value={row.amount}
          disabled={!!row.fromBackend}
          onChange={(_n, v) => update(index, "amount", v)}
          error={errors[`${key}Amount${index}`]}
        />
      </Col>
      <Col md="2" lg="1">
        <SelectFormField
          label={index === 0 ? "Currency" : ""}
          name={`${key}Currency${index}`}
          value={row.currencyId != null ? String(row.currencyId) : ""}
          selectItems={currencies || []}
          getSelectItemId={(i) => i.currencyId}
          getSelectItemName={(i) => i.currencyISO}
          disabled={!!row.fromBackend}
          onChange={(_n, v) => update(index, "currencyId", v)}
          error={errors[`${key}Currency${index}`]}
        />
      </Col>
      <Col md="2">
        <FormField
          label={index === 0 ? "Quote price" : ""}
          name={`${key}Quote${index}`}
          type="number"
          dataTypeDecimalName="currency"
          value={row.cotizacion}
          disabled={!!row.fromBackend}
          onChange={(_n, v) => update(index, "cotizacion", v)}
          error={errors[`${key}Quote${index}`]}
        />
      </Col>
    </React.Fragment>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>Manage income data</ModalHeader>
      <ModalBody className="income-modal-body">
        {/* Los mensajes de validación se posicionan absolutos para no empujar el
            layout ni descuadrar las filas al aparecer/desaparecer mientras se tipea. */}
        <style>{`
          /* Evita el scrollbar horizontal espurio (línea sobre el footer) cuando
             aparece el scroll vertical al agregar filas; las columnas ya se encogen. */
          .income-modal-body { overflow-x: hidden; }
          .income-modal-body .form-group { position: relative; margin-bottom: 0.9rem; }
          .income-modal-body .invalid-feedback {
            position: absolute;
            top: 100%;
            left: 0;
            margin-top: 1px;
            font-size: 11px;
            line-height: 1.15;
          }
          /* Las filas nunca wrappean: las columnas se encogen para entrar todas
             en una línea (incluido el chip de archivo + el botón borrar). */
          .income-modal-body .income-row > [class*="col"] {
            flex-shrink: 1;
            min-width: 0;
          }
          /* Cabezales (labels) centrados sobre su input. */
          .income-modal-body .income-row .form-group label {
            display: block;
            width: 100%;
            text-align: center;
          }
        `}</style>
        {/* Tabs horizontales + filtro "solo activos" a la derecha, en la misma fila. */}
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #e0e0e0", marginBottom: 16, overflowX: "auto" }}>
          {orderedKeys.map((key) => {
            const t = DOC_TYPES.find((d) => d.key === key);
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => goToTab(key)}
                onKeyPress={(e) => (e.key === "Enter" || e.key === " ") && goToTab(key)}
                style={tabStyle(key)}
              >
                <i className={`fa ${t.icon}`} aria-hidden="true" />
                {t.label}
                {sectionHasError(key) ? (
                  <i
                    className="fa fa-exclamation-circle"
                    style={{ color: "#dc3545", fontSize: 12 }}
                    title="This section has errors"
                    aria-hidden="true"
                  />
                ) : (
                  isFilled(key) && (
                    <i className="fa fa-check-circle" style={{ color: ACCENT, fontSize: 12 }} aria-hidden="true" />
                  )
                )}
              </div>
            );
          })}
          {/* Filtro: mostrar solo los incomes activos (IncomeIsActive del backend).
              Solo visible cuando NO hay registros nuevos cargados (los que se ven
              son únicamente los del backend); al agregar uno nuevo, se oculta. */}
          {!hasNewRecords && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", flexShrink: 0, paddingLeft: 12, paddingRight: 16, paddingBottom: 6 }}>
              <label className="switch switch-text switch-pill switch-secondary switch-lg mb-0">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={showOnlyActive}
                  onChange={(e) => setShowOnlyActive(e.target.checked)}
                />
                <span className="switch-label"></span>
                <span className="switch-handle"></span>
              </label>
              <span style={{ marginLeft: "10px", whiteSpace: "nowrap" }}>Relevant for calculation</span>
            </div>
          )}
        </div>

        {errors.general && <div style={{ color: "#dc3545", marginBottom: 12 }}>{errors.general}</div>}

        {/* ---------------- IRP ---------------- */}
        {active === "irp" && (
          <div style={sectionStyle}>
            {visibleRows(irpRows).map(({ row, index }) => (
              <Row key={row.uid} className="align-items-end flex-nowrap income-row">
                <Col md="2">
                  <FormField
                    label={index === 0 ? "Year (YYYY)" : ""}
                    name={`irpYear${index}`}
                    type="text"
                    placeholder="YYYY"
                    value={row.year}
                    disabled={!!row.fromBackend}
                    onChange={(_n, v) => updateIrpRow(index, "year", v)}
                    error={errors[`irpYear${index}`]}
                  />
                </Col>
                {amountCols("irp", row, index, updateIrpRow)}
                {expirationCell(row, index)}
                {fileCell(row, index)}
                {trashCell(() => removeIrpRow(index), !row.fromBackend && irpRows.length <= 1)}
              </Row>
            ))}
            <Button color="secondary" size="sm" outline onClick={() => addRowIfValid("irp", addIrpRow)}>
              <i className="fa fa-plus" /> Add year
            </Button>
            {/* TODO(adjuntos): aquí irá el upload de archivo de respaldo IRP. */}
          </div>
        )}

        {/* ---------------- IVA ---------------- */}
        {active === "iva" && (
          <div style={sectionStyle}>
            {visibleRows(ivaRows).map(({ row, index }) => (
              <Row key={row.uid} className="align-items-end flex-nowrap income-row">
                <Col md="2">
                  <MonthDateField
                    label={index === 0 ? "Month" : ""}
                    name={`ivaMonth${index}`}
                    value={monthToPickerValue(row.month)}
                    disabled={!!row.fromBackend}
                    onChange={(_n, iso) => updateIvaRow(index, "month", pickerToMonth(iso))}
                    error={errors[`ivaMonth${index}`]}
                  />
                </Col>
                {amountCols("iva", row, index, updateIvaRow)}
                {expirationCell(row, index)}
                {fileCell(row, index)}
                {trashCell(() => removeIvaRow(index), !row.fromBackend && ivaRows.length <= IVA_MIN_ROWS)}
              </Row>
            ))}
            <Button
              color="secondary"
              size="sm"
              outline
              onClick={() => addRowIfValid("iva", addIvaRow)}
            >
              <i className="fa fa-plus" /> Add month
            </Button>
            <span style={{ marginLeft: 8, fontSize: 12, color: "#6c757d" }}>
              {visibleRows(ivaRows).length} (min {IVA_MIN_ROWS})
            </span>
            {errors.minIvaCount && (
              <div style={{ color: "#dc3545", marginTop: 10, fontSize: 13 }}>{errors.minIvaCount}</div>
            )}
            {/* TODO(adjuntos): aquí irá el upload de archivos de respaldo IVA. */}
          </div>
        )}

        {/* ---------------- Salary ---------------- */}
        {active === "salario" && (
          <div style={sectionStyle}>
            {visibleRows(salarioRows).map(({ row, index }) => (
              <Row key={row.uid} className="align-items-end flex-nowrap income-row">
                <Col md="2">
                  <MonthDateField
                    label={index === 0 ? "Month" : ""}
                    name={`salarioMonth${index}`}
                    value={monthToPickerValue(row.month)}
                    disabled={!!row.fromBackend}
                    onChange={(_n, iso) => updateSalarioRow(index, "month", pickerToMonth(iso))}
                    error={errors[`salarioMonth${index}`]}
                  />
                </Col>
                {amountCols("salario", row, index, updateSalarioRow)}
                {expirationCell(row, index)}
                {fileCell(row, index)}
                {trashCell(() => removeSalarioRow(index), !row.fromBackend && salarioRows.length <= 1)}
              </Row>
            ))}
            <Button color="secondary" size="sm" outline onClick={() => addRowIfValid("salario", addSalarioRow)}>
              <i className="fa fa-plus" /> Add year
            </Button>
            {/* TODO(adjuntos): aquí irá el upload de archivo de respaldo Salary. */}
          </div>
        )}

        {/* ---------------- Balance (solo Persona Jurídica) ---------------- */}
        {active === "balance" && balanceAvailable && (
          <div style={sectionStyle}>
            {visibleRows(balanceRows).map(({ row, index }) => (
              <Row key={row.uid} className="align-items-end flex-nowrap income-row">
                <Col md="2">
                  <FormField
                    label={index === 0 ? "Year (YYYY)" : ""}
                    name={`balanceYear${index}`}
                    type="text"
                    placeholder="YYYY"
                    value={row.year}
                    disabled={!!row.fromBackend}
                    onChange={(_n, v) => updateBalanceRow(index, "year", v)}
                    error={errors[`balanceYear${index}`]}
                  />
                </Col>
                {amountCols("balance", row, index, updateBalanceRow)}
                {expirationCell(row, index)}
                {fileCell(row, index)}
                {trashCell(() => removeBalanceRow(index), !row.fromBackend && balanceRows.length <= 1)}
              </Row>
            ))}
            <Button color="secondary" size="sm" outline onClick={() => addRowIfValid("balance", addBalanceRow)}>
              <i className="fa fa-plus" /> Add year
            </Button>
            {/* TODO(adjuntos): aquí irá el upload de archivo de respaldo Balance. */}
          </div>
        )}

        {/* ---------------- Extra Income (filas ilimitadas) ---------------- */}
        {active === "extra" && (
          <div style={sectionStyle}>
            {visibleRows(extraRows).map(({ row, index }) => (
              <Row key={row.uid} className="align-items-end flex-nowrap income-row">
                <Col md="3">
                  <FormField
                    label={index === 0 ? "Concept" : ""}
                    name={`extraConcept${index}`}
                    type="text"
                    value={row.concept}
                    disabled={!!row.fromBackend}
                    onChange={(_n, v) => updateExtraRow(index, "concept", v)}
                    error={errors[`extraConcept${index}`]}
                  />
                </Col>
                <Col md="2">
                  <MonthDateField
                    label={index === 0 ? "Month" : ""}
                    name={`extraMonth${index}`}
                    value={monthToPickerValue(row.month)}
                    disabled={!!row.fromBackend}
                    onChange={(_n, iso) => updateExtraRow(index, "month", pickerToMonth(iso))}
                    error={errors[`extraMonth${index}`]}
                  />
                </Col>
                {amountCols("extra", row, index, updateExtraRow, "2")}
                {fileCell(row, index)}
                {trashCell(() => removeExtraRow(index), !row.fromBackend && extraRows.length <= 1)}
              </Row>
            ))}
            <Button color="secondary" size="sm" outline onClick={() => addRowIfValid("extra", addExtraRow)}>
              <i className="fa fa-plus" /> Add extra income
            </Button>
            {/* TODO(adjuntos): aquí irá el upload de archivos de respaldo Extra Income. */}
          </div>
        )}

      </ModalBody>
      <ModalFooter
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fff",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Izquierda: totales recalculados por el backend (RecalculatePersonIncomes). */}
        {recalc ? (
          <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#37474f" }}>
            <span>
              Annual Income:{" "}
              <strong>{formattedNumber(Number(recalc.annual) || 0, "currency", 0)} {currencyLabel}</strong>
            </span>
            <span>
              Extra Annual Income:{" "}
              <strong>{formattedNumber(Number(recalc.extra) || 0, "currency", 0)} {currencyLabel}</strong>
            </span>
          </div>
        ) : (
          <span />
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onRecalculate && (
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <i
                className="fa fa-info-circle"
                style={{ color: "#90a4ae", cursor: "pointer", fontSize: 14 }}
                title="Recalculate only considers non-expired incomes."
                aria-label="Recalculate only considers non-expired incomes."
              />
              <Button color="secondary" outline disabled={recalcLoading || saving} onClick={handleRecalculate}>
                {recalcLoading && <i className="fa fa-spinner fa-spin" />} Recalculate
              </Button>
            </span>
          )}
          <Button color="light" onClick={toggle} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleConfirm} disabled={saving}>
            {saving && <i className="fa fa-spinner fa-spin" />} Confirm
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

PersonIncomeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func, // opcional: recálculo/ponderación en backend
  personType: PropTypes.string, // "Juridical" habilita la sección Balance
  minMonths: PropTypes.number, // antigüedad máxima permitida (parametrizable, default 24)
  currencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currencyLabel: PropTypes.string, // sufijo de moneda para los totales del footer (default "Gs")
  currencies: PropTypes.array, // lista de monedas para el dropdown: [{ currencyId, currencyISO }]
  initialData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]), // incomes ya cargados (GetPersonIncomesData) para prellenar las secciones
};

export default PersonIncomeModal;
